import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { de, enUS } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { parseLocaleNumber } from "utils";
import {
  getNextSectionVisibility,
  getVisibleDayRecords,
  groupRecordsByDate,
} from "utils/monthlyRecords";

const PROVIDER_STYLES = {
  sumup: { label: "SumUp", className: "bg-blue-100 text-blue-700" },
  lightspeed: { label: "Lightspeed", className: "bg-green-100 text-green-700" },
  orderbird: { label: "Orderbird", className: "bg-orange-100 text-orange-700" },
  daily_report: { labelKey: "sales.sourceFileUpload", className: "bg-emerald-100 text-emerald-700" },
};

const CATEGORY_COLORS = {
  Wareneinkauf: "bg-blue-50 text-blue-700",
  Miete: "bg-purple-50 text-purple-700",
  Personal: "bg-green-50 text-green-700",
  "Strom/Gas": "bg-yellow-50 text-yellow-700",
  Versicherungen: "bg-orange-50 text-orange-700",
  Marketing: "bg-pink-50 text-pink-700",
  Reparaturen: "bg-red-50 text-red-700",
  Sonstiges: "bg-slate-100 text-slate-700",
};

const EMPTY_EXPENSE = {
  date: format(new Date(), "yyyy-MM-dd"),
  category: "Wareneinkauf",
  description: "",
  amount: "",
  supplier: "",
  is_recurring: false,
  recurring_period: "Monatlich",
  recurring_month_days: [],
  recurring_weekdays: [],
  invoice_number: "",
};

const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => index + 1);
const WEEKDAY_OPTIONS = [
  { value: 0, labelKey: "finances.weekdays.mon" },
  { value: 1, labelKey: "finances.weekdays.tue" },
  { value: 2, labelKey: "finances.weekdays.wed" },
  { value: 3, labelKey: "finances.weekdays.thu" },
  { value: 4, labelKey: "finances.weekdays.fri" },
  { value: 5, labelKey: "finances.weekdays.sat" },
  { value: 6, labelKey: "finances.weekdays.sun" },
];

function normalizeNumberList(value, min, max) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => parseInt(item, 10))
    .filter((item, index, list) => (
      Number.isInteger(item) && item >= min && item <= max && list.indexOf(item) === index
    ))
    .sort((a, b) => a - b);
}

function toggleNumber(list, value) {
  const normalized = normalizeNumberList(list, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
  if (normalized.includes(value)) {
    return normalized.filter((item) => item !== value);
  }
  return [...normalized, value].sort((a, b) => a - b);
}

function formatRecurringDetail(expense, t) {
  if (!expense?.is_recurring) return "";

  if (expense.recurring_period === "Monatlich") {
    const days = normalizeNumberList(expense.recurring_month_days, 1, 31);
    return days.length > 0 ? `${t("finances.recurringMonthDays")}: ${days.join(", ")}` : "";
  }

  if (expense.recurring_period === "Wöchentlich") {
    const weekdays = normalizeNumberList(expense.recurring_weekdays, 0, 6);
    if (weekdays.length === 0) return "";
    const labels = weekdays.map((weekday) => {
      const option = WEEKDAY_OPTIONS.find((item) => item.value === weekday);
      return option ? t(option.labelKey) : weekday;
    });
    return `${t("finances.recurringWeekdays")}: ${labels.join(", ")}`;
  }

  return "";
}

function SourceBadge({ provider, t }) {
  if (!provider) return <span className="text-xs text-slate-400">{t("sales.sourceManual")}</span>;
  const style = PROVIDER_STYLES[provider];
  if (!style) return <span className="text-xs text-slate-500">{provider}</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}>
      {style.labelKey ? t(style.labelKey) : style.label}
    </span>
  );
}

function ShiftBadge({ shift, t }) {
  const className = shift === "Mittag"
    ? "bg-yellow-100 text-yellow-700"
    : shift === "Abend"
      ? "bg-indigo-100 text-indigo-700"
      : "bg-slate-100 text-slate-600";

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {shift === "Mittag"
        ? t("sales.lunch")
        : shift === "Abend"
          ? t("sales.dinner")
          : shift}
    </span>
  );
}

function LineItemsRow({ sale, t }) {
  const items = sale.line_items || [];
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 py-2">{t("sales.noLineItems")}</p>;
  }

  return (
    <table className="w-full text-xs mt-1">
      <thead>
        <tr className="text-slate-500 border-b">
          <th className="text-left py-1 pr-3">{t("sales.lineItemsName")}</th>
          <th className="text-right py-1 pr-3">{t("sales.lineItemsQty")}</th>
          <th className="text-right py-1 pr-3">{t("sales.lineItemsUnit")}</th>
          <th className="text-right py-1 pr-3">{t("sales.lineItemsTotal")}</th>
          <th className="text-right py-1">{t("sales.lineItemsVat")}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-slate-50 last:border-0">
            <td className="py-1 pr-3 text-slate-700">{item.name}</td>
            <td className="py-1 pr-3 text-right text-slate-600">{parseLocaleNumber(item.quantity)}</td>
            <td className="py-1 pr-3 text-right text-slate-600">€{parseLocaleNumber(item.unit_price).toFixed(2)}</td>
            <td className="py-1 pr-3 text-right font-medium text-slate-800">€{parseLocaleNumber(item.total_price).toFixed(2)}</td>
            <td className="py-1 text-right text-slate-500">{item.tax_rate || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SalesHistorySection({ selectedMonth }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language === "de" ? de : enUS;

  const [showSales, setShowSales] = useState(true);
  const [showExpenses, setShowExpenses] = useState(true);
  const [viewMode, setViewMode] = useState("calendar");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showRecurringOnly, setShowRecurringOnly] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedSalesDays, setExpandedSalesDays] = useState({});
  const [expandedExpenseDays, setExpandedExpenseDays] = useState({});
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [deleteSaleId, setDeleteSaleId] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ ...EMPTY_EXPENSE });

  const { data: sales = [], isLoading } = useQuery({
    queryKey: ["sales"],
    queryFn: () => base44.entities.Sale.list("-date"),
    initialData: [],
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses"],
    queryFn: () => base44.entities.Expense.list("-date"),
    initialData: [],
  });

  const createExpenseMutation = useMutation({
    mutationFn: (data) => base44.entities.Expense.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
    },
  });

  const updateExpenseMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Expense.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id) => base44.entities.Expense.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setDeleteExpenseId(null);
    },
  });

  const deleteSaleMutation = useMutation({
    mutationFn: (id) => base44.entities.Sale.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      setDeleteSaleId(null);
      setExpandedRow(null);
    },
  });

  const resetExpenseForm = () => {
    setExpenseForm({ ...EMPTY_EXPENSE });
    setEditingExpense(null);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date || format(new Date(), "yyyy-MM-dd"),
      category: expense.category || "Wareneinkauf",
      description: expense.description || "",
      amount: expense.amount?.toString() || "",
      supplier: expense.supplier || "",
      is_recurring: expense.is_recurring || false,
      recurring_period: expense.recurring_period || "Monatlich",
      recurring_month_days: normalizeNumberList(expense.recurring_month_days, 1, 31),
      recurring_weekdays: normalizeNumberList(expense.recurring_weekdays, 0, 6),
      invoice_number: expense.invoice_number || "",
    });
    setIsExpenseDialogOpen(true);
  };

  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    const isMonthly = expenseForm.is_recurring && expenseForm.recurring_period === "Monatlich";
    const isWeekly = expenseForm.is_recurring && expenseForm.recurring_period === "Wöchentlich";
    const expenseData = {
      ...expenseForm,
      amount: parseLocaleNumber(expenseForm.amount),
      recurring_period: expenseForm.is_recurring ? expenseForm.recurring_period : "",
      recurring_month_days: isMonthly
        ? normalizeNumberList(expenseForm.recurring_month_days, 1, 31)
        : [],
      recurring_weekdays: isWeekly
        ? normalizeNumberList(expenseForm.recurring_weekdays, 0, 6)
        : [],
    };

    if (editingExpense) {
      updateExpenseMutation.mutate({ id: editingExpense.id, data: expenseData });
    } else {
      createExpenseMutation.mutate(expenseData);
    }
  };

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  const updateVisibility = (section, checked) => {
    const nextVisibility = getNextSectionVisibility(section, checked, showSales, showExpenses);
    setShowSales(nextVisibility.showSales);
    setShowExpenses(nextVisibility.showExpenses);
  };

  const filteredSales = sourceFilter === "all"
    ? sales
    : sourceFilter === "manual"
      ? sales.filter((sale) => !sale.pos_provider)
      : sales.filter((sale) => sale.pos_provider === sourceFilter);

  const monthStart = useMemo(() => startOfMonth(selectedMonth), [selectedMonth]);
  const monthEnd = useMemo(() => endOfMonth(selectedMonth), [selectedMonth]);

  const monthSales = useMemo(() => {
    const records = filteredSales.filter((sale) => {
      const saleDate = parseISO(sale.date);
      return saleDate >= monthStart && saleDate <= monthEnd;
    });

    return [...records].sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      return (a.pos_timestamp || "").localeCompare(b.pos_timestamp || "");
    });
  }, [filteredSales, monthEnd, monthStart]);

  const monthExpenses = useMemo(() => {
    const records = expenses.filter((expense) => {
      const expenseDate = parseISO(expense.date);
      return expenseDate >= monthStart && expenseDate <= monthEnd;
    });

    const visibleRecords = showRecurringOnly
      ? records.filter((expense) => expense.is_recurring)
      : records;

    return [...visibleRecords].sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      return (b.id || 0) - (a.id || 0);
    });
  }, [expenses, monthEnd, monthStart, showRecurringOnly]);

  const salesByDate = useMemo(() => {
    return groupRecordsByDate(monthSales);
  }, [monthSales]);

  const expensesByDate = useMemo(() => {
    return groupRecordsByDate(monthExpenses);
  }, [monthExpenses]);

  const activeDates = useMemo(() => {
    const allDates = new Set();
    if (showSales) monthSales.forEach((sale) => allDates.add(sale.date));
    if (showExpenses) monthExpenses.forEach((expense) => allDates.add(expense.date));
    return Array.from(allDates).sort((a, b) => b.localeCompare(a));
  }, [monthExpenses, monthSales, showExpenses, showSales]);

  const calendarRange = useMemo(() => ({
    monthStart,
    monthEnd,
    gridStart: startOfWeek(monthStart, { weekStartsOn: 1 }),
    gridEnd: endOfWeek(monthEnd, { weekStartsOn: 1 }),
  }), [monthEnd, monthStart]);

  const calendarDays = useMemo(
    () => eachDayOfInterval({ start: calendarRange.gridStart, end: calendarRange.gridEnd }),
    [calendarRange],
  );

  const calendarWeekdays = useMemo(
    () => eachDayOfInterval({
      start: startOfWeek(calendarRange.monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(calendarRange.monthStart, { weekStartsOn: 1 }),
    }).map((day) => format(day, "EEE", { locale })),
    [calendarRange.monthStart, locale],
  );

  const monthRevenue = monthSales.reduce(
    (sum, sale) => sum + (parseLocaleNumber(sale.total_amount) || 0),
    0,
  );
  const monthGuests = monthSales.reduce(
    (sum, sale) => sum + (sale.guests_count || 0),
    0,
  );
  const totalExpenses = monthExpenses.reduce(
    (sum, expense) => sum + (parseLocaleNumber(expense.amount) || 0),
    0,
  );
  const recurringCount = monthExpenses.filter((expense) => expense.is_recurring).length;

  useEffect(() => {
    const monthKey = format(selectedMonth, "yyyy-MM");
    const hasSelectedDateInMonth = selectedCalendarDate?.startsWith(monthKey);
    if (hasSelectedDateInMonth) return;

    setSelectedCalendarDate(activeDates[0] || format(monthStart, "yyyy-MM-dd"));
  }, [activeDates, monthStart, selectedCalendarDate, selectedMonth]);

  const toggleSalesDay = (dateKey) => {
    setExpandedSalesDays((current) => ({ ...current, [dateKey]: !current[dateKey] }));
  };

  const toggleExpenseDay = (dateKey) => {
    setExpandedExpenseDays((current) => ({ ...current, [dateKey]: !current[dateKey] }));
  };

  const renderSalesCell = (dateKey, daySales) => {
    const totalRevenueForDay = daySales.reduce(
      (sum, sale) => sum + (parseLocaleNumber(sale.total_amount) || 0),
      0,
    );
    const totalGuestsForDay = daySales.reduce(
      (sum, sale) => sum + (sale.guests_count || 0),
      0,
    );
    const isExpanded = !!expandedSalesDays[dateKey];
    const visibleSales = getVisibleDayRecords(daySales, isExpanded, 3);

    if (daySales.length === 0) {
      return <span className="text-sm text-slate-400">{t("sales.noSalesOnDay")}</span>;
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-500">
            {daySales.length} {t("sales.salesRecords")} · {totalGuestsForDay} {t("sales.guests")}
          </span>
          <span className="font-semibold text-slate-900">€{totalRevenueForDay.toFixed(2)}</span>
        </div>
        <div className="space-y-2">
          {visibleSales.map((sale) => {
            const hasLineItems = sale.line_items && sale.line_items.length > 0;
            const rowExpanded = expandedRow === sale.id;
            const posTime = sale.pos_timestamp ? sale.pos_timestamp.substring(11, 16) : "—";

            return (
              <div key={sale.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <div
                  className={`${hasLineItems ? "cursor-pointer" : ""} group flex items-start justify-between gap-3`}
                  onClick={() => hasLineItems && toggleRow(sale.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {hasLineItems ? (
                        rowExpanded
                          ? <ChevronDown className="w-4 h-4 text-slate-400" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />
                      ) : (
                        <span className="w-4" />
                      )}
                      <span className="text-sm text-slate-600">{posTime}</span>
                      <ShiftBadge shift={sale.shift} t={t} />
                      <SourceBadge provider={sale.pos_provider} t={t} />
                    </div>
                    <div className="mt-2 flex items-center gap-4 pl-6 text-sm">
                      <span className="text-slate-500">{sale.guests_count} {t("sales.guests")}</span>
                      <span className="font-medium text-slate-900">€{parseLocaleNumber(sale.total_amount).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 justify-end opacity-100 transition-opacity sm:opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={t("sales.delete")}
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeleteSaleId(sale.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {rowExpanded && (
                  <div className="mt-3 border-t border-slate-200 pt-3 pl-6">
                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                      {t("sales.lineItems")}
                    </p>
                    <LineItemsRow sale={sale} t={t} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {daySales.length > 3 && (
          <Button variant="outline" size="sm" onClick={() => toggleSalesDay(dateKey)}>
            {isExpanded ? t("sales.showLess") : t("sales.showMore")}
          </Button>
        )}
      </div>
    );
  };

  const renderExpenseCell = (dateKey, dayExpenses) => {
    const totalAmountForDay = dayExpenses.reduce(
      (sum, expense) => sum + (parseLocaleNumber(expense.amount) || 0),
      0,
    );
    const isExpanded = !!expandedExpenseDays[dateKey];
    const visibleExpenses = getVisibleDayRecords(dayExpenses, isExpanded, 4);

    if (dayExpenses.length === 0) {
      return <span className="text-sm text-slate-400">{t("finances.noExpensesOnDay", "No expenses recorded for this day")}</span>;
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-500">
            {dayExpenses.length} {t("finances.expenseEntries", "Entries")}
          </span>
          <span className="font-semibold text-slate-900">
            {totalAmountForDay.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
          </span>
        </div>
        <div className="space-y-2">
          {visibleExpenses.map((expense) => {
            const recurringDetail = formatRecurringDetail(expense, t);
            return (
              <div key={expense.id} className="group flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className={`text-xs font-normal ${CATEGORY_COLORS[expense.category] || ""}`}>
                      {t(`finances.categories.${expense.category}`, expense.category)}
                    </Badge>
                    {expense.is_recurring && (
                      <Badge variant="outline" className="text-xs">
                        {t(`finances.periods.${expense.recurring_period}`, expense.recurring_period)}
                      </Badge>
                    )}
                    {recurringDetail && (
                      <Badge variant="outline" className="text-xs font-normal">
                        {recurringDetail}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-600">
                    {expense.description || expense.supplier || "\u2014"}
                  </div>
                </div>
                <div className="flex shrink-0 items-start gap-2">
                  <span className="pt-1 text-sm font-semibold text-slate-900">
                    {(parseLocaleNumber(expense.amount) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                  </span>
                  <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={() => handleEditExpense(expense)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteExpenseId(expense.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {dayExpenses.length > 4 && (
          <Button variant="outline" size="sm" onClick={() => toggleExpenseDay(dateKey)}>
            {isExpanded ? t("sales.showLess") : t("sales.showMore")}
          </Button>
        )}
      </div>
    );
  };

  const renderDayGroup = (dateKey) => {
    const daySales = salesByDate.get(dateKey) || [];
    const dayExpenses = expensesByDate.get(dateKey) || [];

    return (
      <div key={dateKey} className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6 py-2">
        <div className="space-y-0.5 md:w-48 md:shrink-0">
          <p className="font-medium text-slate-900">
            {format(parseISO(dateKey), "d MMM yyyy", { locale })}
          </p>
          <p className="text-xs text-slate-500">
            {format(parseISO(dateKey), "EEEE", { locale })}
          </p>
        </div>
        <div className="flex flex-1 min-w-0 flex-col md:flex-row gap-4 md:gap-6">
          {showSales && (
            <div className="min-w-0 flex-1">
              {renderSalesCell(dateKey, daySales)}
            </div>
          )}
          {showExpenses && (
            <div className="min-w-0 flex-1">
              {renderExpenseCell(dateKey, dayExpenses)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">{t("sales.monthlyRecords", "Monthly Records")}</h2>
          <p className="text-xs text-slate-400 mt-1">{format(selectedMonth, "MMMM yyyy", { locale })}</p>
        </div>
        <div className="flex flex-col xl:flex-row xl:items-center gap-3">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
              <Checkbox checked={showExpenses} onCheckedChange={(checked) => updateVisibility("expenses", checked)} />
              {t("finances.expenses")}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
              <Checkbox checked={showSales} onCheckedChange={(checked) => updateVisibility("sales", checked)} />
              {t("sales.title")}
            </label>
          </div>

          <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={viewMode === "table"
                ? "bg-white shadow-sm !text-slate-900 hover:bg-white hover:!text-slate-900"
                : "text-slate-500 hover:text-slate-700"}
              onClick={() => setViewMode("table")}
            >
              {t("sales.viewTable")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={viewMode === "calendar"
                ? "bg-white shadow-sm !text-slate-900 hover:bg-white hover:!text-slate-900"
                : "text-slate-500 hover:text-slate-700"}
              onClick={() => setViewMode("calendar")}
            >
              {t("sales.viewCalendar")}
            </Button>
          </div>

          {showSales && (
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500 whitespace-nowrap">{t("sales.source")}:</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("sales.allSources")}</SelectItem>
                  <SelectItem value="sumup">SumUp</SelectItem>
                  <SelectItem value="lightspeed">Lightspeed</SelectItem>
                  <SelectItem value="orderbird">Orderbird</SelectItem>
                  <SelectItem value="daily_report">{t("sales.sourceFileUpload")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {showExpenses && (
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
              <Checkbox checked={showRecurringOnly} onCheckedChange={setShowRecurringOnly} />
              {t("finances.recurring")}
            </label>
          )}

          <div className="flex items-center gap-2">
            {showExpenses && (
              <Dialog
                open={isExpenseDialogOpen}
                onOpenChange={(open) => {
                  setIsExpenseDialogOpen(open);
                  if (!open) resetExpenseForm();
                }}
              >
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1.5" />
                    {t("finances.addExpense")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingExpense ? t("finances.editExpense") : t("finances.newExpense")}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleExpenseSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expense-date">{t("finances.date")}</Label>
                        <Input
                          id="expense-date"
                          type="date"
                          value={expenseForm.date}
                          onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="expense-category">{t("finances.category")}</Label>
                        <Select
                          value={expenseForm.category}
                          onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
                        >
                          <SelectTrigger id="expense-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(CATEGORY_COLORS).map((category) => (
                              <SelectItem key={category} value={category}>
                                {t(`finances.categories.${category}`, category)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="expense-amount">{t("finances.amount")}</Label>
                        <Input
                          id="expense-amount"
                          type="number"
                          step="0.01"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="expense-supplier">{t("finances.supplier")}</Label>
                        <Input
                          id="expense-supplier"
                          value={expenseForm.supplier}
                          onChange={(e) => setExpenseForm({ ...expenseForm, supplier: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="expense-description">{t("finances.description")}</Label>
                      <Textarea
                        id="expense-description"
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="space-y-3 py-2">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="expense-recurring"
                          checked={expenseForm.is_recurring}
                          onCheckedChange={(checked) => setExpenseForm({
                            ...expenseForm,
                            is_recurring: checked === true,
                            recurring_month_days: checked === true ? expenseForm.recurring_month_days : [],
                            recurring_weekdays: checked === true ? expenseForm.recurring_weekdays : [],
                          })}
                        />
                        <Label htmlFor="expense-recurring" className="text-sm cursor-pointer">
                          {t("finances.recurring")}
                        </Label>
                        {expenseForm.is_recurring && (
                          <Select
                            value={expenseForm.recurring_period}
                            onValueChange={(value) => setExpenseForm({
                              ...expenseForm,
                              recurring_period: value,
                              recurring_month_days: value === "Monatlich" ? expenseForm.recurring_month_days : [],
                              recurring_weekdays: value === "Wöchentlich" ? expenseForm.recurring_weekdays : [],
                            })}
                          >
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Monatlich">{t("finances.monthly")}</SelectItem>
                              <SelectItem value="Wöchentlich">{t("finances.weekly")}</SelectItem>
                              <SelectItem value="Täglich">{t("finances.daily")}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {expenseForm.is_recurring && expenseForm.recurring_period === "Monatlich" && (
                        <div className="space-y-2">
                          <Label className="text-sm text-slate-600">{t("finances.recurringMonthDays")}</Label>
                          <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5">
                            {MONTH_DAY_OPTIONS.map((day) => {
                              const selected = expenseForm.recurring_month_days.includes(day);
                              return (
                                <Button
                                  key={day}
                                  type="button"
                                  variant={selected ? "default" : "outline"}
                                  size="sm"
                                  className={selected ? "h-8 bg-blue-600 hover:bg-blue-700" : "h-8"}
                                  onClick={() => setExpenseForm({
                                    ...expenseForm,
                                    recurring_month_days: toggleNumber(expenseForm.recurring_month_days, day),
                                  })}
                                >
                                  {day}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {expenseForm.is_recurring && expenseForm.recurring_period === "Wöchentlich" && (
                        <div className="space-y-2">
                          <Label className="text-sm text-slate-600">{t("finances.recurringWeekdays")}</Label>
                          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
                            {WEEKDAY_OPTIONS.map((day) => {
                              const selected = expenseForm.recurring_weekdays.includes(day.value);
                              return (
                                <Button
                                  key={day.value}
                                  type="button"
                                  variant={selected ? "default" : "outline"}
                                  size="sm"
                                  className={selected ? "h-8 bg-blue-600 hover:bg-blue-700" : "h-8"}
                                  onClick={() => setExpenseForm({
                                    ...expenseForm,
                                    recurring_weekdays: toggleNumber(expenseForm.recurring_weekdays, day.value),
                                  })}
                                >
                                  {t(day.labelKey)}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                        {t("finances.cancel")}
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                        {editingExpense ? t("finances.update") : t("finances.save")}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-4 sm:p-6">
        {viewMode === "calendar" ? (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {calendarWeekdays.map((weekday) => (
                <div key={weekday} className="px-0 sm:px-2 py-1 text-center sm:text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {weekday}
                </div>
              ))}
              {calendarDays.map((day) => {
                const dateKey = format(day, "yyyy-MM-dd");
                const daySales = salesByDate.get(dateKey) || [];
                const dayExpenses = expensesByDate.get(dateKey) || [];
                const dayRevenue = daySales.reduce((sum, sale) => sum + (parseLocaleNumber(sale.total_amount) || 0), 0);
                const dayExpenseTotal = dayExpenses.reduce((sum, expense) => sum + (parseLocaleNumber(expense.amount) || 0), 0);
                const isSelected = selectedCalendarDate === dateKey;
                const isOutsideMonth = !isSameMonth(day, selectedMonth);

                return (
                  <button
                    key={dateKey}
                    type="button"
                    onClick={() => setSelectedCalendarDate(dateKey)}
                    className={`min-h-[52px] sm:min-h-[88px] md:min-h-[132px] rounded-lg sm:rounded-2xl border p-1 sm:p-2 md:p-3 text-left transition-all ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : isOutsideMonth
                          ? "border-slate-100 bg-slate-50/60 text-slate-300"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1 sm:mb-3">
                      <span className={`text-xs sm:text-sm font-semibold ${isOutsideMonth ? "text-slate-300" : "text-slate-700"}`}>
                        {format(day, "d")}
                      </span>
                      {isToday(day) && (
                        <>
                          <span className="sm:hidden h-1.5 w-1.5 rounded-full bg-slate-900" />
                          <span className="hidden sm:inline rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                            {t("sales.today")}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Mobile: amounts only, shown when > 0 */}
                    <div className="sm:hidden space-y-0.5">
                      {showSales && daySales.length > 0 && (
                        <p className={`text-[11px] font-medium tabular-nums leading-tight ${isOutsideMonth ? "text-slate-300" : "text-emerald-700"}`}>
                          €{dayRevenue.toFixed(0)}
                        </p>
                      )}
                      {showExpenses && dayExpenses.length > 0 && (
                        <p className={`text-[11px] tabular-nums leading-tight ${isOutsideMonth ? "text-slate-300" : "text-rose-600"}`}>
                          €{dayExpenseTotal.toFixed(0)}
                        </p>
                      )}
                    </div>
                    {/* Desktop: original spacious body */}
                    <div className="hidden sm:block space-y-1.5">
                      {showSales && (
                        daySales.length > 0 ? (
                          <div>
                            <p className={`text-xs font-semibold ${isOutsideMonth ? "text-slate-300" : "text-slate-700"}`}>
                              {t("sales.title")}
                            </p>
                            <p className={`text-sm ${isOutsideMonth ? "text-slate-300" : "text-slate-900"}`}>
                              €{dayRevenue.toFixed(0)}
                            </p>
                          </div>
                        ) : (
                          <p className={`text-xs ${isOutsideMonth ? "text-slate-300" : "text-slate-400"}`}>
                            {t("sales.noSalesShort")}
                          </p>
                        )
                      )}
                      {showExpenses && (
                        dayExpenses.length > 0 ? (
                          <div>
                            <p className={`text-xs font-semibold ${isOutsideMonth ? "text-slate-300" : "text-slate-700"}`}>
                              {t("finances.expenses")}
                            </p>
                            <p className={`text-sm ${isOutsideMonth ? "text-slate-300" : "text-slate-900"}`}>
                              {dayExpenseTotal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                            </p>
                          </div>
                        ) : (
                          <p className={`text-xs ${isOutsideMonth ? "text-slate-300" : "text-slate-400"}`}>
                            {t("finances.noExpensesShort", "No expenses")}
                          </p>
                        )
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedCalendarDate ? (
              <div>
                {renderDayGroup(selectedCalendarDate)}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
                {t("sales.noRecordsThisMonth", "No records for this month yet")}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              {showSales && (
                <>
                  <span>{t("sales.monthRevenue")}: <span className="font-medium text-slate-900">€{monthRevenue.toFixed(2)}</span></span>
                  <span>{t("sales.monthGuests")}: <span className="font-medium text-slate-900">{monthGuests}</span></span>
                </>
              )}
              {showExpenses && (
                <>
                  <span>{t("finances.totalExpenses")}: <span className="font-medium text-slate-900">{totalExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span></span>
                  <span>{t("finances.recurring")}: <span className="font-medium text-slate-900">{recurringCount}</span></span>
                </>
              )}
            </div>
            {isLoading ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
                {t("sales.loading")}
              </div>
            ) : activeDates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
                {t("sales.noRecordsThisMonth", "No records for this month yet")}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {activeDates.map((dateKey) => renderDayGroup(dateKey))}
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteExpenseId} onOpenChange={() => setDeleteExpenseId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("finances.deleteExpense")}</AlertDialogTitle>
            <AlertDialogDescription>{t("finances.deleteExpenseDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("finances.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteExpenseMutation.mutate(deleteExpenseId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("finances.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteSaleId} onOpenChange={() => setDeleteSaleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sales.deleteSale")}</AlertDialogTitle>
            <AlertDialogDescription>{t("sales.deleteSaleDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("sales.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteSaleMutation.mutate(deleteSaleId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("sales.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
